import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Briefcase, GraduationCap, Code, Heart, Building, Save, ArrowLeft, Crown, CheckCircle, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

import { userAPI, paymentAPI } from '../utils/api';
import authService from '../firebase/authService';

const UserProfilePage = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tier, setTier] = useState('free');
    const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);

    const [formData, setFormData] = useState({
        identity: 'student',
        university: '',
        degree: '',
        semester: '',
        current_role: '',
        years_of_experience: '',
        industry: '',
        technical_skills: [],
        soft_skills: [],
        organization_code: ''
    });

    const [techInput, setTechInput] = useState('');
    const [softInput, setSoftInput] = useState('');

    useEffect(() => {
        const unsubscribe = authService.onAuthStateChange(async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                try {
                    // Fetch Sub
                    const subRes = await paymentAPI.getUserSubscription();
                    if (subRes?.subscription?.tier) {
                        setTier(subRes.subscription.tier);
                    }

                    // Fetch Profile
                    const profileRes = await userAPI.getProfile();
                    if (profileRes?.profile) {
                        setFormData(prev => ({ ...prev, ...profileRes.profile }));
                    }
                } catch (e) {
                    console.error("Could not fetch profile details", e);
                }
            }
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!user) {
        // Should be protected route, but just in case
        navigate('/');
        return null;
    }

    const handleSave = async () => {
        setSaving(true);
        try {
            await userAPI.updateProfile(formData);

            // Re-fetch Subscription to check if code caused an Enterprise upgrade
            const subRes = await paymentAPI.getUserSubscription();
            if (subRes?.subscription?.tier) {
                const newTier = subRes.subscription.tier;
                if (tier !== 'enterprise' && newTier === 'enterprise' && formData.organization_code) {
                    setShowEnterpriseModal(true);
                } else {
                    toast.success("Profile updated successfully!");
                }
                setTier(newTier);
            } else {
                toast.success("Profile updated successfully!");
            }
        } catch (err) {
            console.error(err);
            toast.error(err.message || "Failed to update profile.");
        } finally {
            setSaving(false);
        }
    };

    const addSkill = (type) => {
        if (type === 'tech' && techInput.trim()) {
            if (!formData.technical_skills.includes(techInput.trim())) {
                setFormData(prev => ({ ...prev, technical_skills: [...prev.technical_skills, techInput.trim()] }));
            }
            setTechInput('');
        } else if (type === 'soft' && softInput.trim()) {
            if (!formData.soft_skills.includes(softInput.trim())) {
                setFormData(prev => ({ ...prev, soft_skills: [...prev.soft_skills, softInput.trim()] }));
            }
            setSoftInput('');
        }
    };

    const removeSkill = (type, skill) => {
        if (type === 'tech') {
            setFormData(prev => ({ ...prev, technical_skills: prev.technical_skills.filter(s => s !== skill) }));
        } else {
            setFormData(prev => ({ ...prev, soft_skills: prev.soft_skills.filter(s => s !== skill) }));
        }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 py-12 px-4 sm:px-6 lg:px-8 font-sans selection:bg-cyan-500/30">
            <div className="max-w-4xl mx-auto space-y-8">

                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <button onClick={() => navigate('/dashboard')} className="text-slate-400 hover:text-white mb-4 flex items-center transition-colors">
                            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
                        </button>
                        <h1 className="text-3xl font-bold text-white flex items-center">
                            <User className="w-8 h-8 mr-3 text-cyan-400" /> My Profile
                        </h1>
                        <p className="text-slate-400 mt-1">Manage your global career identity and skills.</p>
                    </div>

                    <div className={`px-4 py-2 rounded-xl flex items-center gap-2 border ${tier === 'enterprise' ? 'bg-indigo-500/10 border-indigo-500/30 text-indigo-400' : tier === 'plus' ? 'bg-amber-500/10 border-amber-500/30 text-amber-400' : 'bg-slate-800 border-slate-700 text-slate-300'}`}>
                        <Crown className="w-5 h-5" />
                        <span className="font-semibold capitalize">{tier} Plan</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                    {/* Main Column */}
                    <div className="col-span-1 lg:col-span-2 space-y-8">

                        {/* Basic Info */}
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                            <h2 className="text-xl font-semibold text-white mb-6 border-b border-slate-800 pb-4">Career Identity</h2>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-400 mb-2">I am currently a...</label>
                                    <div className="grid grid-cols-3 gap-3">
                                        {['student', 'professional', 'transitioning'].map(id => (
                                            <button
                                                key={id}
                                                onClick={() => setFormData({ ...formData, identity: id })}
                                                className={`py-2 px-4 rounded-lg border text-sm capitalize font-medium transition-all ${formData.identity === id
                                                    ? 'bg-cyan-500/20 border-cyan-500 text-cyan-300'
                                                    : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600'
                                                    }`}
                                            >
                                                {id}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {formData.identity === 'student' ? (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-slate-400 mb-1">University / College</label>
                                            <div className="relative">
                                                <GraduationCap className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
                                                <input type="text" value={formData.university || ''} onChange={e => setFormData({ ...formData, university: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-10 pr-4 py-2.5 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all placeholder-slate-600" placeholder="Stanford University" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Degree</label>
                                            <input type="text" value={formData.degree || ''} onChange={e => setFormData({ ...formData, degree: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 focus:border-cyan-500 outline-none transition-all placeholder-slate-600" placeholder="B.S. Computer Science" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Semester / Year</label>
                                            <input type="text" value={formData.semester || ''} onChange={e => setFormData({ ...formData, semester: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 focus:border-cyan-500 outline-none transition-all placeholder-slate-600" placeholder="Junior / 6th Sem" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Current Role</label>
                                            <div className="relative">
                                                <Briefcase className="absolute left-3 top-3 w-5 h-5 text-slate-500" />
                                                <input type="text" value={formData.current_role || ''} onChange={e => setFormData({ ...formData, current_role: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-10 pr-4 py-2.5 focus:cyan-indigo-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all placeholder-slate-600" placeholder="Senior Frontend Engineer" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Years of Experience</label>
                                            <input type="number" value={formData.years_of_experience || ''} onChange={e => setFormData({ ...formData, years_of_experience: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 focus:border-cyan-500 outline-none transition-all placeholder-slate-600" placeholder="5" />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-400 mb-1">Industry</label>
                                            <input type="text" value={formData.industry || ''} onChange={e => setFormData({ ...formData, industry: e.target.value })} className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-2.5 focus:border-cyan-500 outline-none transition-all placeholder-slate-600" placeholder="Fintech / Healthtech" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </motion.div>

                        {/* Skills */}
                        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                            <h2 className="text-xl font-semibold text-white mb-6 border-b border-slate-800 pb-4">Skills Inventory</h2>

                            <div className="space-y-6">
                                <div>
                                    <label className="flex items-center text-sm font-medium text-slate-400 mb-2">
                                        <Code className="w-4 h-4 mr-2 text-indigo-400" /> Technical Skills
                                    </label>
                                    <div className="flex gap-2 mb-3">
                                        <input type="text" value={techInput} onChange={e => setTechInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSkill('tech')} className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-4 py-2 focus:border-indigo-500 outline-none transition-all" placeholder="e.g. React, Python, AWS" />
                                        <button onClick={() => addSkill('tech')} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-white font-medium transition-colors">Add</button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {formData.technical_skills.map(skill => (
                                            <span key={skill} className="px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 rounded-full text-sm flex items-center">
                                                {skill}
                                                <button onClick={() => removeSkill('tech', skill)} className="ml-2 text-indigo-400 hover:text-white">&times;</button>
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="flex items-center text-sm font-medium text-slate-400 mb-2">
                                        <Heart className="w-4 h-4 mr-2 text-pink-400" /> Soft Skills
                                    </label>
                                    <div className="flex gap-2 mb-3">
                                        <input type="text" value={softInput} onChange={e => setSoftInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addSkill('soft')} className="flex-1 bg-slate-950 border border-slate-800 rounded-lg px-4 py-2 focus:border-pink-500 outline-none transition-all" placeholder="e.g. Leadership, Communication" />
                                        <button onClick={() => addSkill('soft')} className="px-4 py-2 bg-pink-600 hover:bg-pink-500 rounded-lg text-white font-medium transition-colors">Add</button>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {formData.soft_skills.map(skill => (
                                            <span key={skill} className="px-3 py-1 bg-pink-500/10 border border-pink-500/20 text-pink-300 rounded-full text-sm flex items-center">
                                                {skill}
                                                <button onClick={() => removeSkill('soft', skill)} className="ml-2 text-pink-400 hover:text-white">&times;</button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">

                        {/* B2B Code */}
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="bg-gradient-to-b from-slate-800 to-slate-900 border border-slate-700 rounded-2xl p-6 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/10 blur-3xl rounded-full"></div>

                            <h3 className="text-lg font-bold text-white mb-2 flex items-center">
                                <Building className="w-5 h-5 mr-2 text-cyan-400" /> Institution Code
                            </h3>
                            <p className="text-sm text-slate-400 mb-4">
                                If your university or company gave you an invite code, enter it below to unlock <strong>Enterprise</strong> features and join your cohort.
                            </p>

                            <div className="space-y-3">
                                <input
                                    type="text"
                                    value={formData.organization_code || ''}
                                    onChange={e => setFormData({ ...formData, organization_code: e.target.value.toUpperCase() })}
                                    className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-cyan-300 font-mono tracking-widest text-center focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none transition-all"
                                    placeholder="ORG-CODE"
                                />
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="w-full py-3 bg-slate-700 hover:bg-slate-600 text-white font-semibold rounded-lg transition-colors border border-slate-600"
                                >
                                    Join Organization
                                </button>
                            </div>
                        </motion.div>

                        {/* Save Button Widget */}
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 sticky top-6">
                            <h3 className="font-semibold text-white mb-4">Save Changes</h3>
                            <p className="text-sm text-slate-400 mb-6">Update your global profile. This information is used to personalize your AI Roadmaps.</p>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="w-full py-3 bg-gradient-to-r from-cyan-500 to-indigo-500 hover:from-cyan-400 hover:to-indigo-400 text-white font-bold rounded-xl shadow-lg shadow-cyan-500/20 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                            >
                                {saving ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <>
                                        <Save className="w-5 h-5" /> <span>Save Profile</span>
                                    </>
                                )}
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            {/* Enterprise Success Modal */}
            {showEnterpriseModal && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        className="bg-gradient-to-br from-slate-900 to-indigo-950 border-2 border-indigo-500/50 max-w-lg w-full rounded-3xl p-8 shadow-2xl relative overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/20 blur-[100px] rounded-full pointer-events-none"></div>
                        <div className="absolute bottom-0 left-0 w-48 h-48 bg-indigo-500/20 blur-[80px] rounded-full pointer-events-none"></div>

                        <div className="text-center relative z-10 space-y-6">
                            <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-full mx-auto flex items-center justify-center shadow-lg shadow-emerald-500/30">
                                <CheckCircle className="w-10 h-10 text-white" />
                            </div>

                            <div>
                                <h2 className="text-3xl font-bold text-white mb-2 font-mono">Enterprise Unlocked!</h2>
                                <p className="text-indigo-200">You successfully joined your cohort. Your account has been upgraded with unlimited premium features.</p>
                            </div>

                            <div className="bg-slate-900/50 border border-indigo-500/30 rounded-2xl p-5 text-left space-y-3">
                                <h3 className="font-semibold text-white text-sm uppercase mb-3 text-center tracking-widest">What you can do now</h3>
                                <div className="flex items-center gap-3 text-slate-300">
                                    <div className="p-1.5 bg-cyan-500/20 rounded-lg"><Code className="w-4 h-4 text-cyan-400" /></div>
                                    <span className="text-sm">AI Portfolio Project Generator</span>
                                </div>
                                <div className="flex items-center gap-3 text-slate-300">
                                    <div className="p-1.5 bg-indigo-500/20 rounded-lg"><Save className="w-4 h-4 text-indigo-400" /></div>
                                    <span className="text-sm">Unlimited Custom Roadmaps</span>
                                </div>
                                <div className="flex items-center gap-3 text-slate-300">
                                    <div className="p-1.5 bg-emerald-500/20 rounded-lg"><User className="w-4 h-4 text-emerald-400" /></div>
                                    <span className="text-sm">Full Interview Prep Suite</span>
                                </div>
                            </div>

                            <button
                                onClick={() => {
                                    setShowEnterpriseModal(false);
                                    navigate('/dashboard');
                                }}
                                className="w-full py-4 bg-white text-indigo-950 font-bold rounded-xl hover:bg-slate-200 transition-all shadow-xl hover:shadow-indigo-500/25 flex items-center justify-center gap-2"
                            >
                                Enter Dashboard <ArrowRight className="w-5 h-5" />
                            </button>
                        </div>
                    </motion.div>
                </div>
            )}
        </div>
    );
};

export default UserProfilePage;
